import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import Env from '../../shared/config/envConfig'
import {
  Grid,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Link,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TableSortLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import DownloadIcon from '@mui/icons-material/Download'
import SendIcon from '@mui/icons-material/Send'
import PaymentsIcon from '@mui/icons-material/Payments'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { addCsrfHeaders } from '../../shared/config/csrf'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

// Keeping CheckboxComponent available if needed later, but MUI Checkbox is fine for simple layout
// import CheckboxComponent from '@3m5/crude-frontend/dist/shared/components/FormComponents/CheckboxComponent'

// Types
interface MailTemplate {
  id?: string
  key?: string
  name?: string
  subject?: string
  body?: string
  text?: string
  content?: string
  type?: string
}

interface InvoiceRow {
  mitgliedsnummer: string
  firmenname: string
  mitgliedsform: string
  contactEmail: string
  rechnungsnummer: string
  salutation: string
  invoiceId: string
  selected: boolean
  sentAt?: string | Date
  amountGross: number
  paid: boolean
  amountOpen: number
}

// API response types for annual generation
interface MembershipType {
  name: string
  membershipKind: 'INDIVIDUAL' | 'CORPORATE' | string
  admissionFee: number
  contactCount: number
  annualFee: number
  uuid: string
  isNew: boolean
  id: string
}

interface AnnualGenerationDetail {
  membershipId: string
  membershipNumber: string
  companyName: string
  invoiceNumber: string
  contactEmail: string
  invoiceId: string
  membershipType: MembershipType
  salutation: string
  net: number
  tax: number
  gross: number
  file: string | null
  pdfGenerated: boolean
  sent?: Date
  createdOrUpdated: 'created' | 'updated' | 'existing'
  paid: boolean
  amountGross: number
  amountOpen?: number
}

interface AnnualGenerationResponse {
  year?: number
  runDate?: string
  totalInvoices?: number
  totalNet?: number
  totalTax?: number
  totalGross?: number
  details: AnnualGenerationDetail[]
}

const mapDetailToRow = (d: AnnualGenerationDetail): InvoiceRow => ({
  mitgliedsnummer: d.membershipNumber || d.membershipId || '-',
  firmenname: d.companyName,
  mitgliedsform: d.membershipType?.name || '',
  rechnungsnummer: d.invoiceNumber,
  contactEmail: d.contactEmail,
  salutation: d.salutation,
  invoiceId: d.invoiceId,
  sentAt: d.sent,
  selected: !d.sent,
  amountGross: d.amountGross,
  paid: d.paid,
  amountOpen: d.amountOpen || 0,
})

// Helper: parse membership number to numeric value if possible
const parseMembershipNumber = (s: string): { num: number | null, raw: string } => {
  const raw = (s ?? '').toString()
  // Try to keep digits only for numeric comparison
  const digits = raw.replace(/[^0-9]/g, '')
  if (digits.length > 0) {
    const n = Number(digits)
    if (!Number.isNaN(n)) return { num: n, raw }
  }
  return { num: null, raw }
}

// Sorting utilities
type SortKey = 'mitgliedsnummer' | 'firmenname' | 'mitgliedsform' | 'contactEmail' | 'rechnungsnummer' | 'sentAt' | 'amountOpen'
type SortDir = 'asc' | 'desc'

const compareMembershipNumber = (a: string, b: string): number => {
  const ap = parseMembershipNumber(a)
  const bp = parseMembershipNumber(b)
  if (ap.num !== null && bp.num !== null) {
    if (ap.num !== bp.num) return ap.num - bp.num
  } else if (ap.num !== null) {
    return -1
  } else if (bp.num !== null) {
    return 1
  }
  return ap.raw.localeCompare(bp.raw, undefined, { numeric: true, sensitivity: 'base' })
}

const compareDates = (a?: string | Date, b?: string | Date): number => {
  const da = a ? new Date(a).getTime() : NaN
  const db = b ? new Date(b).getTime() : NaN
  const aValid = !Number.isNaN(da)
  const bValid = !Number.isNaN(db)
  if (aValid && bValid) return da - db
  if (aValid) return -1
  if (bValid) return 1
  return 0
}

const sortRowsWith = (arr: InvoiceRow[], key: SortKey, dir: SortDir, groupSentLast: boolean): InvoiceRow[] => {
  const factor = dir === 'asc' ? 1 : -1
  return [...arr].sort((a, b) => {
    if (groupSentLast) {
      const aSent = !!a.sentAt
      const bSent = !!b.sentAt
      if (aSent !== bSent) return aSent ? 1 : -1 // unsent first
    }

    let cmp = 0
    switch (key) {
      case 'mitgliedsnummer':
        cmp = compareMembershipNumber(a.mitgliedsnummer, b.mitgliedsnummer)
        break
      case 'firmenname':
        cmp = (a.firmenname || '').localeCompare(b.firmenname || '', undefined, { numeric: true, sensitivity: 'base' })
        break
      case 'mitgliedsform':
        cmp = (a.mitgliedsform || '').localeCompare(b.mitgliedsform || '', undefined, { sensitivity: 'base' })
        break
      case 'contactEmail':
        cmp = (a.contactEmail || '').localeCompare(b.contactEmail || '', undefined, { sensitivity: 'base' })
        break
      case 'rechnungsnummer':
        cmp = (a.rechnungsnummer || '').localeCompare(b.rechnungsnummer || '', undefined, { numeric: true, sensitivity: 'base' })
        break
      case 'sentAt':
        cmp = compareDates(a.sentAt, b.sentAt)
        break
      case 'amountOpen':
        cmp = a.amountOpen - b.amountOpen
        break
      default:
        cmp = 0
    }

    if (cmp === 0 && key !== 'mitgliedsnummer') {
      // Stable-ish secondary sort by membership number
      cmp = compareMembershipNumber(a.mitgliedsnummer, b.mitgliedsnummer)
    }

    return cmp * factor
  })
}

const useStyles = makeStyles(() => ({
  root: {
    marginTop: '24px',
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginBottom: '16px',
  },
  tableHeadCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
}))

const Invoice: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId?: string }>()
  const classes = useStyles()

  const [allYearsData, setAllYearsData] = useState<AnnualGenerationResponse[]>([])
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  const [rows, setRows] = useState<InvoiceRow[]>([])
  const [loadingAction, setLoadingAction] = useState<string | null>(null)
  const [showZipOffer, setShowZipOffer] = useState<boolean>(false)
  const [zipData, setZipData] = useState<{ blob: Blob, filename: string } | null>(null)
  const [sendSummaryOpen, setSendSummaryOpen] = useState<boolean>(false)
  const [sendSummaryMsg, setSendSummaryMsg] = useState<string>('')
  // Default-Mailtemplates
  const [mailTemplates, setMailTemplates] = useState<any[]>([])
  // Jahr der Rechnungsrunde (für Platzhalterersetzung)
  const [year, setYear] = useState<number | null>(null)

  // Sorting state
  const [sortKey, setSortKey] = useState<SortKey>('mitgliedsnummer')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [groupSentLast, setGroupSentLast] = useState<boolean>(true)

  const applySort = useCallback((arr: InvoiceRow[]) => sortRowsWith(arr, sortKey, sortDir, groupSentLast), [sortKey, sortDir, groupSentLast])
  const handleSort = (key: SortKey) => {
    setGroupSentLast(false) // bei manuellem Sortieren reine Spaltensortierung ohne Gruppierung
    setSortKey(prevKey => {
      if (prevKey === key) {
        setSortDir(prevDir => (prevDir === 'asc' ? 'desc' : 'asc'))
        return prevKey
      }
      setSortDir('asc')
      return key
    })
    // rows neu sortieren
    setRows(prev => sortRowsWith(prev, key, (key === sortKey ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'), false))
  }

  const handleYearChange = (newYear: number) => {
    setSelectedYear(newYear)
    setYear(newYear)
    const yearData = allYearsData.find(y => y.year === newYear)
    const mapped = (yearData?.details || []).map(mapDetailToRow)
    setRows(applySort(mapped))
  }

  // Dialog state for per-row email sending
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<InvoiceRow | null>(null)
  const [emailText, setEmailText] = useState<string>('')
  const [sendingSingle, setSendingSingle] = useState<boolean>(false)
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState<string>('')
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false)

  const allChecked = useMemo(() => rows.length > 0 && rows.every(r => r.selected), [rows])
  const someChecked = useMemo(() => rows.some(r => r.selected) && !allChecked, [rows, allChecked])
  const selectedCount = useMemo(() => rows.filter(r => r.selected).length, [rows])

  // Quill Editor configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  }), [])

  const quillFormats = useMemo(() => [
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet',
    'link',
  ], [])

  const loadMembershipInvoices = useCallback(async () => {
    try {
      setLoadingAction('load')
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/json')
      addCsrfHeaders(headers)
      const url = `${Env.basePath || ''}/api/admin/invoices/annual/membership`
      const res = await fetch(url, { method: 'GET', credentials: 'include', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${text}`)
      }
      const json: AnnualGenerationResponse[] = await res.json().catch(() => [])
      setAllYearsData(json)
      // Wir behalten das aktuell ausgewählte Jahr bei
      const yearToSelect = json.find(y => y.year === selectedYear) ? selectedYear : (json[0]?.year || selectedYear)
      setSelectedYear(yearToSelect)

      const yearData = json.find(y => y.year === yearToSelect)
      const mapped = (yearData?.details || []).map(mapDetailToRow)
      setRows(applySort(mapped))
      setYear(yearToSelect)

      console.log('Mitgliedsrechnungen geladen:', {
        years: json.map(j => j.year),
        selectedYear: yearToSelect,
      })

      // Default-Mailtemplates laden (best-effort)
      try {
        const tHeaders: HeadersInit = new Headers()
        tHeaders.set('Accept', 'application/json')
        addCsrfHeaders(tHeaders)
        const tUrl = `${Env.basePath || ''}/api/admin/mailtemplates/`
        const tRes = await fetch(tUrl, { method: 'GET', credentials: 'include', headers: tHeaders })
        if (tRes.ok) {
          const tJson = await tRes.json().catch(() => [])
          const arr: MailTemplate[] = Array.isArray(tJson) ? tJson : (tJson?.items || [])
          setMailTemplates(arr)
          console.log('Mailtemplates geladen:', { count: arr?.length ?? 0 })
        }
      } catch (err) {
        console.warn('Fehler beim Laden der Mailtemplates', err)
      }
    } catch (e) {
      console.error('Fehler beim Laden der Mitgliedsrechnungen', e)
      setRows([])
    } finally {
      setLoadingAction(null)
    }
  }, [selectedYear, applySort])

  // Beim Laden der Komponente bestehende Mitgliedsrechnungen laden
  useEffect(() => {
    loadMembershipInvoices()
  }, [])

  const toggleRowEmail = (invoiceId: string) => {
    setRows(prev => prev.map(r => (r.invoiceId === invoiceId ? { ...r, selected: !r.selected } : r)))
  }

  // Ersetzt Platzhalter im Template-HTML
  const applyMailPlaceholders = (html: string, row: InvoiceRow, yr: number | null): string => {
    try {
      const sal = (row.salutation || '').toString()
      const yearStr = (yr ?? new Date().getFullYear()).toString()
      let out = html || ''
      // Einfache Klammern {salutation} / {saluation} (alias) / {year}
      out = out.replace(/\{\s*salutation\s*\}/gi, sal)
      out = out.replace(/\{\s*saluation\s*\}/gi, sal)
      out = out.replace(/\{\s*year\s*\}/gi, yearStr)
      return out
    } catch {
      return html || ''
    }
  }

  const getDefaultEmailText = (row: InvoiceRow): string => {
    // Wenn Templates vorhanden sind, verwende zuerst das Template mit type === 'MEMBER_INVOICE'.
    // Fallback: heuristische Suche über key/name, danach erstes Template.
    // Erwartete Felder: subject/body oder text/content (je nach Backendmodell)
    const pickBody = (t?: MailTemplate) => (t?.body || t?.text || t?.content || '').trim()
    let bodyFromTemplate = ''
    if (mailTemplates && mailTemplates.length > 0) {
      const byType = mailTemplates.find(t => (t as MailTemplate)?.type === 'MEMBER_INVOICE') as MailTemplate | undefined
      const preferred = byType || (mailTemplates.find(t => {
        const key = (t?.key || t?.name || '').toLowerCase()
        return key.includes('invoice') || key.includes('rechnung') || key.includes('membership') || key.includes('mitglied')
      }) as MailTemplate | undefined) || (mailTemplates[0] as MailTemplate)
      bodyFromTemplate = pickBody(preferred)
    }

    if (bodyFromTemplate) return applyMailPlaceholders(bodyFromTemplate, row, year)

    // Fallback-Text (mit Platzhalterersetzung, falls im Text vorhanden)
    const fallback = `Hallo ${row.firmenname},\n\nbitte finden Sie im Anhang Ihre Mitgliedsrechnung ${row.rechnungsnummer}.\n\nBei Rückfragen stehen wir Ihnen gerne zur Verfügung.\n\nMit freundlichen Grüßen\nIhr MarkenCamp Team`
    return applyMailPlaceholders(fallback, row, year)
  }

  const openSendDialog = (row: InvoiceRow) => {
    setActiveRow(row)
    setEmailText(getDefaultEmailText(row))
    setSendDialogOpen(true)
  }

  const closeSendDialog = () => {
    setSendDialogOpen(false)
    setActiveRow(null)
    setEmailText('')
    setSendingSingle(false)
  }

  const handleConfirmSendSingle = async () => {
    if (!activeRow) return
    try {
      setSendingSingle(true)

      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/json')
      headers.set('Content-Type', 'application/json')
      addCsrfHeaders(headers)

      const body = {
        invoiceId: activeRow.invoiceId,
        alternativeBody: emailText,
        alternativeSubject: null,
      }
      const url = `${Env.basePath || ''}/api/admin/invoices/annual/membership/mail`
      const res = await fetch(url, { body: JSON.stringify(body), method: 'POST', credentials: 'include', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${text}`)
      }

      // Nach Erfolg optional in der Tabelle abwählen und Versanddatum setzen
      const nowIso = new Date().toISOString()
      setRows(prev => applySort(prev.map(r => (r.invoiceId === activeRow.invoiceId ? { ...r, selected: false, sentAt: nowIso } : r))))

      // Feedback anzeigen
      setSendSummaryMsg(`E-Mail versendet: ${activeRow.rechnungsnummer}`)
      setSendSummaryOpen(true)

      // Dialog schließen
      setSendDialogOpen(false)
    } catch (e) {
      console.error('Fehler beim Einzelversand', e)
      setSendSummaryMsg('Einzelversand fehlgeschlagen. Bitte prüfen Sie die Konsole für Details.')
      setSendSummaryOpen(true)
    } finally {
      setSendingSingle(false)
    }
  }

  const openPaymentDialog = (row: InvoiceRow) => {
    setActiveRow(row)
    setPaymentAmount('')
    setPaymentDate(new Date().toISOString().split('T')[0])
    setPaymentDialogOpen(true)
  }

  const closePaymentDialog = () => {
    setPaymentDialogOpen(false)
    if (!sendDialogOpen) setActiveRow(null)
  }

  const handleCreatePayment = async () => {
    if (!activeRow || !paymentAmount) return
    setIsSubmittingPayment(true)
    try {
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/json')
      addCsrfHeaders(headers)
      const url = `${Env.basePath || ''}/api/payments/create?invoiceId=${encodeURIComponent(activeRow.invoiceId)}&amount=${encodeURIComponent(paymentAmount)}&date=${encodeURIComponent(paymentDate)}`
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
      })
      if (res.ok) {
        setSendSummaryMsg('Zahlung wurde erfolgreich erfasst.')
        setSendSummaryOpen(true)
        closePaymentDialog()
        // Daten neu laden
        loadMembershipInvoices()
      } else {
        const text = await res.text().catch(() => '')
        alert(`Fehler beim Erfassen der Zahlung: ${res.status} ${text}`)
      }
    } catch (e) {
      console.error(e)
      alert('Fehler beim Erfassen der Zahlung.')
    } finally {
      setIsSubmittingPayment(false)
    }
  }

  const toggleAllEmail = () => {
    const shouldCheckAll = !(rows.length > 0 && rows.every(r => r.selected))
    setRows(prev => prev.map(r => ({ ...r, selected: shouldCheckAll })))
  }

  // Handlers — endpoints are placeholders; wire to real API when available
  const handleGenerateAll = async () => {
    try {
      setLoadingAction('generate')
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/json')
      headers.set('Content-Type', 'application/json')
      addCsrfHeaders(headers)
      const url = `${Env.basePath || ''}/api/admin/invoices/annual`
      const res = await fetch(url, { method: 'POST', credentials: 'include', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        console.error('Fehler beim Generieren der Jahresrechnungen', res.status, text)
        throw new Error(`HTTP ${res.status}`)
      }
      const json: AnnualGenerationResponse = await res.json().catch(() => ({ details: [] }))
      const mapped = (json.details || []).map(mapDetailToRow)
      setRows(applySort(mapped))
      console.log('Jahresrechnungen geladen:', {
        total: json.totalInvoices ?? mapped.length,
        runDate: json.runDate,
      })
    } catch (e) {
      // Optional: Snackbar integrieren; vorerst Konsole
      console.error(e)
      setRows([])
    } finally {
      setLoadingAction(null)
    }
  }

  const handleDownloadAll = async () => {
    try {
      setLoadingAction('download')
      setShowZipOffer(false)
      setZipData(null)
      const url = `${Env.basePath || ''}/api/admin/invoices/annual/zip`
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/zip, application/octet-stream')
      addCsrfHeaders(headers)

      const res = await fetch(url, { method: 'GET', credentials: 'include', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`ZIP Download fehlgeschlagen: HTTP ${res.status} ${text}`)
      }

      const disposition = res.headers.get('Content-Disposition') || res.headers.get('content-disposition')
      let filename = 'Mitgliedsrechnungen.zip'
      if (disposition) {
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition)
        const raw = decodeURIComponent(match?.[1] || '') || match?.[2]
        if (raw) filename = raw
      }

      const blob = await res.blob()
      setZipData({ blob, filename })
      // Erst nach erfolgreichem Laden dem User anbieten
      setShowZipOffer(true)
    } catch (e) {
      console.error(e)
      alert('ZIP konnte nicht heruntergeladen werden.')
    } finally {
      setLoadingAction(null)
    }
  }

  const doDownloadAllZip = () => {
    if (!zipData) return
    try {
      createAndDownloadFileFromBlob(zipData.blob, zipData.filename)
    } finally {
      setShowZipOffer(false)
      setZipData(null)
    }
  }

  const handleSendSelected = async () => {
    try {
      setLoadingAction('send')
      const toSend = rows.filter(r => r.selected)
      if (toSend.length === 0) return

      // Gleiche Logik wie beim Einzelversand: JSON-Body an denselben Endpoint senden
      // Hier ohne alternativen Betreff/Text – es wird nur die incoiceId (sic) übermittelt

      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/json')
      headers.set('Content-Type', 'application/json')
      addCsrfHeaders(headers)

      const promises = toSend.map(async (r) => {
        const url = `${Env.basePath || ''}/api/admin/invoices/annual/membership/mail`
        const body = { invoiceId: r.invoiceId }
        const res = await fetch(url, { method: 'POST', credentials: 'include', headers, body: JSON.stringify(body) })
        if (!res.ok) {
          const text = await res.text().catch(() => '')
          throw new Error(`HTTP ${res.status} ${text}`)
        }
        return r.invoiceId
      })

      const results = await Promise.allSettled(promises)
      const succeededIds = results.filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled').map(r => r.value)
      const succeeded = succeededIds.length
      const failed = results.length - succeeded

      // Erfolgreich versendete Zeilen abwählen und Versanddatum setzen
      if (succeededIds.length > 0) {
        const nowIso = new Date().toISOString()
        setRows(prev => applySort(prev.map(r => (succeededIds.includes(r.invoiceId) ? { ...r, selected: false, sentAt: nowIso } : r))))
      }

      setSendSummaryMsg(`E-Mails versendet: ${succeeded}. Fehlgeschlagen: ${failed}.`)
      setSendSummaryOpen(true)
    } catch (e) {
      console.error('Fehler beim Versenden ausgewählter Rechnungen', e)
      setSendSummaryMsg('Versand fehlgeschlagen. Bitte prüfen Sie die Konsole für Details.')
      setSendSummaryOpen(true)
    } finally {
      setLoadingAction(null)
    }
  }

  // Downloads the PDF response (binary) using helper createAndDownloadFileFromBlob
  const openPdfInNewTab = async (invoiceId: string) => {
    try {
      setLoadingAction('pdf')
      const url = `${Env.basePath || ''}/api/admin/invoices/annual/membership/pdf?invoiceId=${encodeURIComponent(invoiceId)}`
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/pdf')
      addCsrfHeaders(headers)

      const res = await fetch(url, { method: 'POST', credentials: 'include', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`PDF Download fehlgeschlagen: HTTP ${res.status} ${text}`)
      }
      const blob = await res.blob()
      const filename = `Rechnung-${invoiceId}.pdf`
      createAndDownloadFileFromBlob(blob, filename)
    } catch (e) {
      console.error(e)
      alert('PDF konnte nicht geladen werden.')
    } finally {
      setLoadingAction(null)
    }
  }

  const createAndDownloadFileFromBlob = (data: Blob, filename: string): void => {
    const dataUrl = window.URL.createObjectURL(data)
    const tempLink = document.createElement('a')
    tempLink.href = dataUrl
    tempLink.setAttribute('download', filename)
    tempLink.click()
  }

  const formatDateTime = (d?: string | Date): string => {
    if (!d) return '–'
    const dt = new Date(d)
    if (isNaN(dt.getTime())) return '–'
    try {
      // Nur Datum, keine Uhrzeit anzeigen
      return dt.toLocaleDateString()
    } catch {
      // Fallback: ISO nur Datumsteil
      return dt.toISOString().slice(0, 10)
    }
  }

  const renderInvoiceLink = (row: InvoiceRow) => {
    return (
      <Link
        href='#' underline='hover' onClick={(e: any) => {
          e.preventDefault()
          openPdfInNewTab(row.invoiceId)
        }}
      >
        {row.rechnungsnummer}
      </Link>
    )
  }

  useEffect(() => {
    if (invoiceId && rows.length > 0) {
      const targetRow = rows.find(r => r.invoiceId === invoiceId)
      if (targetRow) {
        openPaymentDialog(targetRow)
      }
    }
  }, [invoiceId, rows])

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12}>
        <h2>Mitgliedsrechnungen</h2>
      </Grid>
      <Stack direction='row' className={classes.toolbar} spacing={2}>
        <FormControl size='small' style={{ minWidth: 120 }}>
          <InputLabel id='year-select-label'>Jahr</InputLabel>
          <Select
            labelId='year-select-label'
            value={selectedYear}
            label='Jahr'
            onChange={(e) => handleYearChange(e.target.value as number)}
          >
            {allYearsData.map((y) => (
              <MenuItem key={y.year} value={y.year}>
                {y.year}
              </MenuItem>
            ))}
            {allYearsData.length === 0 && (
              <MenuItem value={new Date().getFullYear()}>
                {new Date().getFullYear()}
              </MenuItem>
            )}
          </Select>
        </FormControl>
        <Button
          variant='contained'
          color='primary'
          startIcon={<AutoAwesomeIcon />}
          onClick={handleGenerateAll}
          disabled={loadingAction !== null}
        >
          Alle Mitgliedsrechnungen neu generieren
        </Button>
        <Button
          variant='outlined'
          color='primary'
          startIcon={<DownloadIcon />}
          onClick={handleDownloadAll}
          disabled={loadingAction !== null}
        >
          Alle Rechnungen herunterladen
        </Button>
        <Button
          variant='contained'
          color='secondary'
          startIcon={<SendIcon />}
          onClick={handleSendSelected}
          disabled={loadingAction !== null || selectedCount === 0}
        >
          {`Ausgewählte Rechnungen absenden (${selectedCount})`}
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell} align='center'>
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={toggleAllEmail}
                  inputProps={{ 'aria-label': 'Alle auswählen' }}
                />
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={sortKey === 'mitgliedsnummer'}
                  direction={sortKey === 'mitgliedsnummer' ? sortDir : 'asc'}
                  onClick={() => handleSort('mitgliedsnummer')}
                >
                  Mitgliedsnummer
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={sortKey === 'firmenname'}
                  direction={sortKey === 'firmenname' ? sortDir : 'asc'}
                  onClick={() => handleSort('firmenname')}
                >
                  Firmenname
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={sortKey === 'mitgliedsform'}
                  direction={sortKey === 'mitgliedsform' ? sortDir : 'asc'}
                  onClick={() => handleSort('mitgliedsform')}
                >
                  Mitgliedsform
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={sortKey === 'contactEmail'}
                  direction={sortKey === 'contactEmail' ? sortDir : 'asc'}
                  onClick={() => handleSort('contactEmail')}
                >
                  E-Mail
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={sortKey === 'rechnungsnummer'}
                  direction={sortKey === 'rechnungsnummer' ? sortDir : 'asc'}
                  onClick={() => handleSort('rechnungsnummer')}
                >
                  Rechnungsnummer
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={sortKey === 'sentAt'}
                  direction={sortKey === 'sentAt' ? sortDir : 'asc'}
                  onClick={() => handleSort('sentAt')}
                >
                  Versanddatum
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <TableSortLabel
                  active={sortKey === 'amountOpen'}
                  direction={sortKey === 'amountOpen' ? sortDir : 'asc'}
                  onClick={() => handleSort('amountOpen')}
                >
                  offener Betrag (brutto)
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadCell}>Status</TableCell>
              <TableCell className={classes.tableHeadCell} align='right'>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.invoiceId} hover>
                <TableCell align='center'>
                  <Checkbox
                    checked={row.selected}
                    onChange={() => toggleRowEmail(row.invoiceId)}
                    inputProps={{ 'aria-label': `Per Mail senden: ${row.rechnungsnummer}` }}
                  />
                </TableCell>
                <TableCell>{row.mitgliedsnummer}</TableCell>
                <TableCell>{row.firmenname}</TableCell>
                <TableCell>{row.mitgliedsform}</TableCell>
                <TableCell>{row.contactEmail}</TableCell>
                <TableCell>{renderInvoiceLink(row)}</TableCell>
                <TableCell>{formatDateTime(row.sentAt)}</TableCell>
                <TableCell>{row.amountOpen.toFixed(2)} €</TableCell>
                <TableCell>
                  {row.paid ? (
                    <Chip label='Bezahlt' color='success' size='small' />
                  ) : row.amountOpen < row.amountGross && row.amountOpen > 0 ? (
                    <Chip label='Teilweise offen' color='warning' size='small' />
                  ) : (
                    <Chip label='Offen' color='primary' size='small' />
                  )}
                </TableCell>
                <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                  <Button
                    variant='text'
                    color='secondary'
                    size='small'
                    startIcon={<SendIcon />}
                    onClick={() => openSendDialog(row)}
                    disabled={loadingAction !== null || (sendingSingle && activeRow?.invoiceId === row.invoiceId)}
                  >
                    Rechnung absenden
                  </Button>
                  <Button
                    variant='text'
                    color='primary'
                    size='small'
                    startIcon={<PaymentsIcon />}
                    onClick={() => openPaymentDialog(row)}
                    disabled={loadingAction !== null}
                    sx={{ ml: 1 }}
                  >
                    Zahlung
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={sendDialogOpen} onClose={closeSendDialog} fullWidth maxWidth='sm'>
        <DialogTitle>Rechnung per E-Mail senden
          {activeRow && (
            <div style={{ fontSize: 12, fontWeight: 400, color: '#666', marginTop: 4 }}>
              Empfänger: {activeRow.contactEmail || '–'} • Rechnungsnummer: {activeRow.rechnungsnummer || '–'}
            </div>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {activeRow && (
            <>
              <div style={{ marginTop: 16 }}>
                <ReactQuill
                  theme='snow'
                  value={emailText}
                  onChange={(html) => setEmailText(html)}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSendDialog} disabled={sendingSingle}>Abbrechen</Button>
          <Button
            variant='contained'
            color='secondary'
            startIcon={<SendIcon />}
            onClick={handleConfirmSendSingle}
            disabled={sendingSingle}
          >
            E-Mail versenden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paymentDialogOpen} onClose={closePaymentDialog} fullWidth maxWidth='xs'>
        <DialogTitle>Zahlungseingang erfassen
          {activeRow && (
            <div style={{ fontSize: 12, fontWeight: 400, color: '#666', marginTop: 4 }}>
              Rechnungsnummer: {activeRow.rechnungsnummer || '–'} • Offener Betrag: {activeRow.amountOpen.toFixed(2)} €
            </div>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label='Betrag'
              type='number'
              fullWidth
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              InputProps={{ inputProps: { step: '0.01' } }}
              required
            />
            <TextField
              label='Datum'
              type='date'
              fullWidth
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentDialog} disabled={isSubmittingPayment}>Abbrechen</Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleCreatePayment}
            disabled={isSubmittingPayment || !paymentAmount || !paymentDate}
          >
            Zahlung speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showZipOffer}
        onClose={() => setShowZipOffer(false)}
        autoHideDuration={8000}
        message='Alle Rechnungen als ZIP herunterladen?'
        action={
          <Button
            color='primary'
            startIcon={<DownloadIcon />}
            onClick={() => {
              setShowZipOffer(false)
              doDownloadAllZip()
            }}
            disabled={loadingAction === 'download' || !zipData}
          >
            ZIP herunterladen
          </Button>
        }
      />

      <Snackbar
        open={sendSummaryOpen}
        onClose={() => setSendSummaryOpen(false)}
        autoHideDuration={6000}
        message={sendSummaryMsg}
      />
    </Grid>
  )
}
export default Invoice
